import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { FloppyDiskIcon } from '@phosphor-icons/react/dist/csr/FloppyDisk';
import { XIcon } from '@phosphor-icons/react/dist/csr/X';

import { Button } from '../../design-system/primitives/Button/Button';
import { Textarea } from '../../design-system/primitives/Textarea/Textarea';
import { Dialog } from '../../design-system/composites/Dialog/Dialog';
import { Input } from '../../design-system/primitives/Input/Input';
import { Select } from '../../design-system/primitives/Select/Select';
import {
  ApiError,
  NETWORK_ERROR_CODE,
  PAGE_EXPIRED_CODE,
  TOO_MANY_REQUESTS_CODE,
} from '../../lib/api/client';
import {
  replacementsApi,
  type ReplacementDeviceType,
  type ReplacementRow,
} from '../../lib/api/replacements';
import { mapDetails } from '../reference/referenceFormHelpers';
import styles from './ReplacementFormDialog.module.css';

const FORBIDDEN_CODE = 'forbidden';

/**
 * `POST /replacements` (M-13, Task 5, spec R-1, R-2, R-5).
 *
 * ## ONE DEVICE KIND PER REPLACEMENT, and a patient needing both is two calls
 *
 * The legacy form offered two CHECKBOXES and flattened them with
 * `implode(',', $_POST['Type'])` (`callCenter-add-new-replacement.php:88`),
 * so one row could read `Type = 'Reader,Sensor'` under ONE `Status` column —
 * dismissing the reader half necessarily dismissed the sensor half. (And
 * because `implode()` on a missing key is a fatal in PHP 8 while both boxes
 * were optional, an agent who ticked neither got a white screen.)
 *
 * A radio-style `Select` here, one row per call, is what makes the two
 * halves independently fulfillable. A caller who needs both submits twice.
 *
 * ## `case_number` must match a real case — and the server says so, not this form
 *
 * Nothing here checks the case exists. `StoreReplacementRequest` answers a
 * `422` naming `case_number`, and this form renders that against the field.
 * The legacy's own check (`check_caseNumber_replacement.php`) counted rows
 * in `replacements` rather than in `patients`, so it answered "have we
 * replaced against this case before" and never "does this case exist" — and
 * it was unauthenticated and injectable besides.
 *
 * ## The serial is free text, deliberately (spec R-5)
 *
 * The agent is reading it off a broken device over a phone line. It is kept
 * as text and linked to a registered device only when it happens to match.
 * Refusing the request because the serial was illegible would be worse than
 * the missing link — which is the opposite of `DispenseDialog`, where a
 * REAL device is required, because that one is a person physically handing
 * something over.
 */

export interface ReplacementFormDialogProps {
  open: boolean;
  /** Pre-fills and locks the case number — the case-detail panel passes it so a replacement raised from a case cannot land on a different one. */
  caseNumber?: string;
  onClose: () => void;
  onSaved: (created: ReplacementRow) => void;
}

interface FormState {
  caseNumber: string;
  deviceType: ReplacementDeviceType;
  oldSerialNumber: string;
  perceptionCode: string;
  assessmentCode: string;
  libreType: string;
  reason: string;
}

const BLANK: FormState = {
  caseNumber: '',
  deviceType: 'sensor',
  oldSerialNumber: '',
  perceptionCode: '',
  assessmentCode: '',
  libreType: '',
  reason: '',
};

/** `''` becomes `null`, never the empty string — the API's optional fields are `nullable`, and a blank string is not the same fact as "not recorded". */
function blankToNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

export function ReplacementFormDialog({ open, caseNumber, onClose, onSaved }: ReplacementFormDialogProps) {
  const { t } = useTranslation();

  const [form, setForm] = useState<FormState>(BLANK);
  const [missingCase, setMissingCase] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const inFlight = useRef(false);

  useEffect(() => {
    if (!open) return;
    setForm({ ...BLANK, caseNumber: caseNumber ?? '' });
    setMissingCase(false);
    setFailure(null);
    setFieldErrors({});
  }, [open, caseNumber]);

  function set<K extends keyof FormState>(key: K, value: FormState[K]): void {
    setForm((current) => ({ ...current, [key]: value }));
  }

  function describeFailure(error: ApiError): string {
    switch (error.code) {
      case FORBIDDEN_CODE:
        return t('replacements.notPermittedToCreate');
      case TOO_MANY_REQUESTS_CODE:
        return t('replacements.throttled');
      case PAGE_EXPIRED_CODE:
        return t('auth.sessionExpired');
      case NETWORK_ERROR_CODE:
        return t('auth.unreachable');
      default:
        return t('replacements.createFailed');
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (inFlight.current) return;

    if (form.caseNumber.trim() === '') {
      setMissingCase(true);
      return;
    }
    setMissingCase(false);

    inFlight.current = true;
    setSubmitting(true);
    setFailure(null);
    setFieldErrors({});

    try {
      const created = await replacementsApi.create({
        case_number: form.caseNumber.trim(),
        device_type: form.deviceType,
        old_serial_number: blankToNull(form.oldSerialNumber),
        perception_code: blankToNull(form.perceptionCode),
        assessment_code: blankToNull(form.assessmentCode),
        libre_type: blankToNull(form.libreType),
        reason: blankToNull(form.reason),
      });
      onSaved(created);
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;

      const mapped = mapDetails(error.details);
      const known = new Set([
        'case_number',
        'device_type',
        'old_serial_number',
        'perception_code',
        'assessment_code',
        'libre_type',
        'reason',
      ]);

      // Anything the form has no field for goes to the banner rather than
      // being silently dropped — the identical split the sibling dialogs
      // document.
      const banner = Object.entries(mapped)
        .filter(([field]) => !known.has(field))
        .map(([, message]) => message);

      setFieldErrors(mapped);

      if (banner.length > 0) {
        setFailure(banner.join(' '));
      } else if (Object.keys(mapped).length === 0) {
        setFailure(describeFailure(error));
      }
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} kind="form" size="md" onClose={onClose} title={t('replacements.newReplacementTitle')}>
      <form
        className={styles['form']}
        method="post"
        noValidate
        onSubmit={(event) => {
          void handleSubmit(event);
        }}
      >
        <div className={styles['failure']} role="alert" aria-atomic="true">
          {failure}
        </div>

        <Input
          label={t('replacements.fieldCase')}
          width="fill"
          dir="ltr"
          required
          readOnly={caseNumber !== undefined}
          value={form.caseNumber}
          error={missingCase ? t('replacements.required') : (fieldErrors['case_number'] ?? undefined)}
          onChange={(event) => {
            const { value } = event.target;
            set('caseNumber', value);
            setMissingCase(false);
          }}
        />

        {/* ONE KIND PER ROW — see this file's own docblock for why this is a
            single-value control and not the legacy's pair of checkboxes. */}
        <Select
          label={t('replacements.fieldDeviceType')}
          width="fill"
          required
          value={form.deviceType}
          options={[
            { value: 'sensor', label: t('replacements.deviceType.sensor') },
            { value: 'reader', label: t('replacements.deviceType.reader') },
          ]}
          error={fieldErrors['device_type'] ?? undefined}
          hint={t('replacements.deviceTypeHint')}
          onChange={(event) => {
            set('deviceType', event.target.value as ReplacementDeviceType);
          }}
        />

        <Input
          label={t('replacements.fieldOldSerial')}
          width="fill"
          dir="ltr"
          value={form.oldSerialNumber}
          error={fieldErrors['old_serial_number'] ?? undefined}
          hint={t('replacements.oldSerialHint')}
          onChange={(event) => {
            set('oldSerialNumber', event.target.value);
          }}
        />

        <Input
          label={t('replacements.fieldLibreType')}
          width="fill"
          value={form.libreType}
          error={fieldErrors['libre_type'] ?? undefined}
          onChange={(event) => {
            set('libreType', event.target.value);
          }}
        />

        <Input
          label={t('replacements.fieldPerceptionCode')}
          width="fill"
          dir="ltr"
          value={form.perceptionCode}
          error={fieldErrors['perception_code'] ?? undefined}
          onChange={(event) => {
            set('perceptionCode', event.target.value);
          }}
        />

        <Input
          label={t('replacements.fieldAssessmentCode')}
          width="fill"
          dir="ltr"
          value={form.assessmentCode}
          error={fieldErrors['assessment_code'] ?? undefined}
          onChange={(event) => {
            set('assessmentCode', event.target.value);
          }}
        />

        <Textarea
          label={t('replacements.fieldReason')}
          dir="auto"
          value={form.reason}
          error={fieldErrors['reason']}
          onChange={(event) => {
            set('reason', event.target.value);
          }}
        />

        <div className={styles['actions']}>
          <Button type="submit" loading={submitting} icon={<FloppyDiskIcon size={16} />}>
            {t('replacements.createSubmit')}
          </Button>
          <Button type="button" variant="ghost" onClick={onClose} icon={<XIcon size={16} />}>
            {t('replacements.cancel')}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
